import { Calendar, Clock, CheckCircle2, XCircle } from 'lucide-react';
import type { CountStatus, InventoryCountItem } from './types';

export const STATUS_CONFIG: Record<
  CountStatus,
  { label: string; color: string; icon: typeof Calendar }
> = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700', icon: Calendar },
  in_progress: { label: 'In progress', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  canceled: { label: 'Canceled', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

export function formatRelativeTime(date: string): string {
  const now = new Date();
  const d = new Date(date);
  const diff = d.getTime() - now.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days < 0) {
    const absDays = Math.abs(days);
    if (absDays === 0) return 'Today';
    if (absDays === 1) return 'Yesterday';
    if (absDays < 7) return `${absDays} days ago`;
    if (absDays < 14) return 'last week';
    if (absDays < 30) return `${Math.floor(absDays / 7)} weeks ago`;
    if (absDays < 60) return 'last month';
    if (absDays < 90) return '2 months ago';
    if (absDays < 120) return '3 months ago';
    if (absDays < 180) return `${Math.floor(absDays / 30)} months ago`;
    if (absDays < 365) return 'last quarter';
    if (absDays < 730) return 'last year';
    return `${Math.floor(absDays / 365)} years ago`;
  }
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 7) return `In ${days} days`;
  return d.toLocaleDateString();
}

export function formatDateTime(date: string): string {
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatDate(date: string): string {
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Categorize count items into uncounted, unmatched, matched, and all.
 */
export function categorizeItems(items: InventoryCountItem[]) {
  const uncounted = items.filter((item) => item.counted_quantity === null);
  const matched = items.filter(
    (item) => item.counted_quantity !== null && item.counted_quantity === item.expected_quantity
  );
  const unmatched = items.filter(
    (item) => item.counted_quantity !== null && item.counted_quantity !== item.expected_quantity
  );
  return { uncounted, unmatched, matched, all: items };
}

/**
 * Calculate totals for a set of count items.
 */
export function calculateTotals(items: InventoryCountItem[]) {
  let totalExpected = 0;
  let totalCounted = 0;
  let totalVarianceUnits = 0;
  let totalVarianceCost = 0;
  let countedItems = 0;

  items.forEach((item) => {
    totalExpected += item.expected_quantity;
    if (item.counted_quantity !== null) {
      totalCounted += item.counted_quantity;
      countedItems += 1;
      totalVarianceUnits += (item.variance ?? 0);
      totalVarianceCost += (item.variance_cost ?? 0);
    }
  });

  return {
    totalExpected,
    totalCounted,
    totalVarianceUnits,
    totalVarianceCost,
    countedItems,
    totalItems: items.length,
  };
}

/**
 * Generate a CSV string from count items.
 */
export function generateCSV(
  items: InventoryCountItem[],
  countName: string,
  locationName: string
): string {
  const header = 'Product,SKU,Expected,Counted,Variance (Units),Variance (Cost)';
  const rows = items.map((item) => {
    const name = (item.product?.name || 'Unknown').replace(/,/g, ' ');
    const sku = item.product?.sku || '';
    const expected = item.expected_quantity;
    const counted = item.counted_quantity ?? '-';
    const variance = item.variance ?? '-';
    const varianceCost = item.variance_cost ?? '-';
    return `"${name}","${sku}",${expected},${counted},${variance},${varianceCost}`;
  });

  const meta = [
    `"Count: ${countName}"`,
    `"Location: ${locationName}"`,
    `"Generated: ${new Date().toLocaleString()}"`,
    '',
  ];

  return [...meta, header, ...rows].join('\n');
}

/**
 * Trigger a file download in the browser.
 */
export function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
